import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import { ImgCotacao } from "@components/Feed/CotacaoInfo";
import { useSelector } from "react-redux";
import formatDate from "@utils/formatDate";
import { green, red } from "@material-ui/core/colors";

const useStyles = makeStyles(() => ({
  watermarkWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 0,
    pointerEvents: "none",
    userSelect: "none",
  },
  watermarkText: {
    fontSize: "2rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
}));

function hexToRgb(hex) {
  const fullHex =
    hex.length === 4 ? "#" + [...hex.slice(1)].map((x) => x + x).join("") : hex;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : "0, 0, 0";
}

const isToday = (date) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
};

const getFormattedDate = (date) =>
  new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const Watermark = ({ isSmall }) => {
  const classes = useStyles();
  const theme = useTheme();
  const color = `rgba(${hexToRgb(theme.palette.text.primary)}, 0.3)`;
  const smallStyles = isSmall ? { fontSize: "1.5rem" } : {};

  return (
    <div className={classes.watermarkWrapper}>
      <Typography
        className={classes.watermarkText}
        style={{ color, ...smallStyles }}
      >
        cafetrade.com.br
      </Typography>
    </div>
  );
};

const renderPriceChange = (change) => {
  const num = Number(change);
  const isPositive = !isNaN(num) && num > 0;
  const isNegative = !isNaN(num) && num < 0;

  return (
    <span
      style={{
        color: isPositive ? green[500] : isNegative ? red[500] : "inherit",
      }}
    >
      {isNaN(num) ? change : `${num > 0 ? "+" : ""}${num.toFixed(2)}`}
    </span>
  );
};

const renderRow = (v, isLondres = false, useLastDate = false) => {
  const data = useLastDate ? v.yesterdayVencimento || {} : v;
  const yesterdayOI = v?.yesterdayVencimento?.openInterest ?? "-";

  return (
    <TableRow key={v.contractSymbol}>
      <TableCell>
        <Box display="flex" alignItems="center" gridGap={4}>
          <ImgCotacao isLondres={isLondres} />
          {data.contractSymbol}
        </Box>
      </TableCell>
      <TableCell style={{ fontWeight: "bold" }}>
        {data.lastPrice ?? "-"}
      </TableCell>
      <TableCell>{renderPriceChange(data.priceChange)}</TableCell>
      <TableCell>{data.openPrice ?? "-"}</TableCell>
      <TableCell>{data.highPrice ?? "-"}</TableCell>
      <TableCell>{data.lowPrice ?? "-"}</TableCell>
      <TableCell>{data.previousPrice ?? "-"}</TableCell>
      <TableCell>{data.volume ?? "-"}</TableCell>
      <TableCell>{data.openInterest ?? "-"}</TableCell>
      <TableCell>{yesterdayOI}</TableCell>
      <TableCell>
        {data.tradeTime
          ? typeof data.tradeTime === "number"
            ? new Date(data.tradeTime * 1000).toLocaleString()
            : formatDate(data.updatedAt, false, true)
          : "-"}
      </TableCell>
    </TableRow>
  );
};

export default function Contrato() {
  const [cotacao] = useSelector((states) => [states.cotacaoReducer]);

  const arabicaList = [];
  const robustaList = [];
  Object.values(cotacao?.vencimentos || {})
    .filter((v) => !v.expired)
    .forEach((v) => {
      if (v.preSymbol === "RM") {
        robustaList.push(v);
      } else {
        arabicaList.push(v);
      }
    });
  const processList = (list) => {
    const allUpdatedToday = list.every((v) => isToday(v.updatedAt));
    const useLastDate = !allUpdatedToday;

    let referenceDate;

    if (useLastDate) {
      const lastDate = list.reduce((latest, v) => {
        const rawDate = v.yesterdayVencimento?.updatedAt;
        const updated = rawDate ? new Date(rawDate) : new Date(0);
        return updated > latest ? updated : latest;
      }, new Date(0));
      referenceDate = getFormattedDate(lastDate);
    } else {
      referenceDate = getFormattedDate(new Date());
    }

    return { list, useLastDate, referenceDate, allUpdatedToday };
  };
  const arabica = processList(arabicaList);
  const robusta = processList(robustaList);

  const renderTable = (title, data, isLondres) => (
    <TableContainer
      component={Paper}
      style={{ position: "relative", marginTop: 40 }}
    >
      <Watermark />
      {!data.allUpdatedToday ? (
        <Box p={2}>
          <Typography color="textSecondary">
            🕒 Aguardando fechamento de hoje: {getFormattedDate(new Date())}
          </Typography>
        </Box>
      ) : null}
      <Table size="small" style={{ position: "relative", zIndex: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell colSpan={10}>
              <Box display="flex" alignItems="center" gridGap={4}>
                <ImgCotacao isLondres={isLondres} />
                <Typography variant="h6">
                  {title} –{" "}
                  {data.useLastDate
                    ? `Última atualização: ${data.referenceDate}`
                    : data.referenceDate}
                </Typography>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>Contrato</TableCell>
            <TableCell>Último Preço</TableCell>
            <TableCell>Variação</TableCell>
            <TableCell>Abertura</TableCell>
            <TableCell>Máximo</TableCell>
            <TableCell>Mínimo</TableCell>
            <TableCell>Anterior</TableCell>
            <TableCell>Volume</TableCell>
            <TableCell>Open Int</TableCell>
            <TableCell>
              OI {getFormattedDate(new Date(Date.now() - 86400000))}{" "}
            </TableCell>
            <TableCell>Data / Hora</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.list.map((v) => renderRow(v, isLondres, data.useLastDate))}
        </TableBody>
      </Table>
    </TableContainer>
  );
  return (
    <Box mt={3} display="flex" flexDirection="column" gap={4}>
      <Typography variant="h6" gutterBottom>
        Contratos
      </Typography>

      {renderTable("Arabica", arabica, false)}
      {renderTable("Robusta", robusta, true)}
    </Box>
  );
}
